import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, AlertCircle, ArrowRight } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function BillingSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  
  const [status, setStatus] = useState("checking"); // checking, active, pending, error
  const [message, setMessage] = useState("Verifica del pagamento in corso...");
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 15; // 30 seconds total (2s interval)

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      setMessage("Sessione di pagamento non trovata.");
      return;
    }

    const verifyPayment = async () => {
      try {
        const response = await axios.get(
          `${API_URL}/api/billing/verify-session?session_id=${sessionId}`,
          {
            headers: {
              "Origin": window.location.origin
            }
          }
        );

        const { status: paymentStatus, message: responseMessage } = response.data;

        if (paymentStatus === "ACTIVE") {
          setStatus("active");
          setMessage("Pagamento confermato! Il tuo abbonamento è attivo.");
          // Redirect to login after 3 seconds
          setTimeout(() => navigate("/login"), 3000);
        } else if (paymentStatus === "ERROR") {
          setStatus("error");
          setMessage(responseMessage || "Errore nella verifica del pagamento.");
        } else {
          // Still pending, retry if not max attempts
          if (attempts < maxAttempts) {
            setAttempts(prev => prev + 1);
            setTimeout(verifyPayment, 2000);
          } else {
            setStatus("pending");
            setMessage("Il pagamento è ancora in elaborazione. Controlla la tua email per conferma.");
          }
        }
      } catch (error) {
        console.error("Verification error:", error);
        if (attempts < maxAttempts) {
          setAttempts(prev => prev + 1);
          setTimeout(verifyPayment, 2000);
        } else {
          setStatus("error");
          setMessage("Impossibile verificare il pagamento. Controlla la tua email.");
        }
      }
    };

    verifyPayment();
  }, [sessionId, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-emerald-50 flex items-center justify-center p-4">
      <Card className="max-w-md w-full shadow-xl border-0">
        <CardContent className="p-8 text-center">
          {status === "checking" && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-blue-100 flex items-center justify-center">
                <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-2">Verifica in corso</h1>
              <p className="text-slate-600 mb-4">{message}</p>
              <div className="w-full bg-slate-200 rounded-full h-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(attempts / maxAttempts) * 100}%` }}
                />
              </div>
            </>
          )}

          {status === "active" && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-emerald-100 flex items-center justify-center animate-bounce">
                <CheckCircle2 className="w-12 h-12 text-emerald-600" />
              </div>
              <h1 className="text-2xl font-bold text-emerald-700 mb-2">Pagamento Confermato!</h1>
              <p className="text-slate-600 mb-6">{message}</p>
              <p className="text-sm text-slate-500 mb-4">Reindirizzamento al login...</p>
              <Button 
                onClick={() => navigate("/login")}
                className="w-full bg-emerald-600 hover:bg-emerald-700"
              >
                Vai al Login <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </>
          )}

          {status === "pending" && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-amber-100 flex items-center justify-center">
                <Loader2 className="w-10 h-10 text-amber-600" />
              </div>
              <h1 className="text-2xl font-bold text-amber-700 mb-2">Elaborazione in corso</h1>
              <p className="text-slate-600 mb-6">{message}</p>
              <Button 
                onClick={() => navigate("/login")}
                variant="outline"
                className="w-full"
              >
                Vai al Login
              </Button>
            </>
          )}

          {status === "error" && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-100 flex items-center justify-center">
                <AlertCircle className="w-12 h-12 text-red-600" />
              </div>
              <h1 className="text-2xl font-bold text-red-700 mb-2">Errore</h1>
              <p className="text-slate-600 mb-6">{message}</p>
              <div className="space-y-3">
                <Button 
                  onClick={() => navigate("/register")}
                  className="w-full"
                >
                  Riprova la registrazione
                </Button>
                <Link to="/login" className="block text-blue-600 hover:underline">
                  Torna al login
                </Link>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
